import { Controller } from "@hotwired/stimulus";
import * as Luxon from "luxon";

export default class extends Controller {
  static targets = [
    "dayButton",
    "eligibilityDate",
    "loadingContainer",
    "locale",
    "nextButtonSlots",
    "potentialSlotsContainer",
    "timeButton",
    "zoneIds",
  ];

  connect() {
    this.initializeState();
  }

  initializeState() {
    const noFutureSlots =
      this.element.dataset.driveTestDashboardNoFutureSlots === "true";
    const hasVisibleSlots =
      document.querySelector("#potential_slots .SlotPicker-Time") !== null;

    if (noFutureSlots) {
      this.toggleDashboardNoSlots(true);
      this.displayDashboardWeekMessage(false);
      this.setArrowButtonState(
        document.getElementById("slots-picker-previous-week-button"),
        true
      );
      this.setArrowButtonState(
        document.getElementById("slots-picker-next-week-button"),
        true
      );
      if (this.hasNextButtonSlotsTarget) {
        this.nextButtonSlotsTarget.disabled = true;
      }
    } else if (hasVisibleSlots) {
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(false);
      this.disableScheduleButton();
    } else {
      this.toggleDashboardNoSlots(false);
      this.displayDashboardWeekMessage(true);
      this.disableScheduleButton();
    }

    this.updateDashboardNavigationButtons();
  }

  async showLoader() {
    if (this.hasPotentialSlotsContainerTarget) {
      this.potentialSlotsContainerTarget.classList.add("hidden");
    }
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.remove("hidden");
    }
  }

  async hideLoader() {
    if (this.hasPotentialSlotsContainerTarget) {
      this.potentialSlotsContainerTarget.classList.remove("hidden");
    }
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.add("hidden");
    }
  }

  toggleActiveDay(event) {
    event.preventDefault();

    if (event.currentTarget.classList.contains("Button--disabled")) {
      return;
    }

    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    document.getElementById("picked_slot_day").value =
      event.currentTarget.textContent.trim();
    this.resetPickedSlotFields();
  }

  toggleActiveTime(event) {
    event.preventDefault();

    this.timeButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    event.currentTarget.classList.add("isActive");

    const slotIsoTime = event.currentTarget.getAttribute("data-slot-iso-time");
    const slotId = event.currentTarget.getAttribute("data-slot-id");
    const isMasterLesson =
      event.currentTarget.getAttribute("data-is-master-lesson") || "false";
    document.getElementById("picked_slot_time").value =
      event.currentTarget.textContent.trim();
    document.getElementById("picked_slot_iso_time").value = slotIsoTime;
    document.getElementById("picked_slot_id").value = slotId;
    document.getElementById("picked_slot_is_master_lesson").value =
      isMasterLesson;
    document.getElementById("picked_slot_master_lesson_id").value = "";
    this.enableScheduleButton();
  }

  formatDate(inputStr) {
    const dateStr = inputStr.split(" ")[1];
    let [month, day, year] = dateStr.split("/");

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const currentDay = currentDate.getDate();

    year =
      currentMonth > month || (currentMonth == month && currentDay > day)
        ? currentYear + 1
        : currentYear;

    return `${year}_${month}_${day}`;
  }

  formatDateToString(date) {
    return this.formatStringDate(date, "-");
  }

  formatDateToStringForSlots(date) {
    return this.formatStringDate(date, "_");
  }

  formatStringDate(date, separator) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}${separator}${month}${separator}${day}`;
  }

  parseDateString(dateString) {
    const cleanedDateString = dateString.replace(/"/g, "");
    const [year, month, day] = cleanedDateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  async get_drive_test_slots(event, start_date = null) {
    await this.showLoader();

    let formattedDate;
    if (start_date !== null) {
      if (typeof start_date === "string" && /^\d{4}_\d{2}_\d{2}$/.test(start_date)) {
        formattedDate = start_date;
      } else {
        formattedDate = this.formatDateToStringForSlots(new Date(start_date));
      }
    } else {
      const activeDayButton = document.querySelector(".SlotPicker-Day.isActive");
      formattedDate =
        event?.currentTarget?.dataset?.slotDate ||
        activeDayButton?.dataset?.slotDate ||
        this.formatDate(document.getElementById("picked_slot_day").value);
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const schoolToken = document
      .getElementById("us-school-token")
      .innerHTML.trim();

    const automaticPreferenceInput = document.getElementById(
      "driving_school_booking_gearbox_preference_automatic"
    );
    const isAutomaticSelected = automaticPreferenceInput
      ? automaticPreferenceInput.checked
      : false;

    const locale = this.getLocale();
    const zoneIds = this.hasZoneIdsTarget ? this.zoneIdsTarget.value : "";
    const eligibilityDate = this.hasEligibilityDateTarget
      ? this.eligibilityDateTarget.value
      : null;

    const response = await fetch(
      `/us/drive-test-slots/${schoolToken}/${formattedDate}?is_automatic_transmission=${isAutomaticSelected}&instructor_id=Any&locale=${locale}&dashboard=true`,
      {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          driving_school_token: schoolToken,
          start_date: formattedDate,
          test_center_zone_ids: zoneIds,
          eligibility_date: eligibilityDate,
        }),
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("potential_slots");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }

      if (targetElement) {
        const hasSlots = targetElement.querySelector(".SlotPicker-Time") !== null;
        this.displayDashboardWeekMessage(!hasSlots);
        this.toggleDashboardNoSlots(false);
        this.clearSelectedSlot();
      }
    }

    await this.hideLoader();
  }

  async getNextWeekStartDate(dateString) {
    let [year, month, day] = dateString.split("-");
    let nextWeekStartDate = new Date(year, month - 1, day);
    nextWeekStartDate.setDate(nextWeekStartDate.getDate() + 7);
    nextWeekStartDate.setHours(0, 0, 0, 0);
    return nextWeekStartDate;
  }

  async getPreviousWeekStartDate(dateString) {
    let [year, month, day] = dateString.split("-");
    let previousWeekStartDate = new Date(year, month - 1, day);
    previousWeekStartDate.setDate(previousWeekStartDate.getDate() - 7);
    previousWeekStartDate.setHours(0, 0, 0, 0);
    return previousWeekStartDate;
  }

  async slotsNextWeek(event) {
    event.preventDefault();
    if (event.currentTarget.disabled) {
      return;
    }

    const startingDate = this.getDisplayedWeekStartDate(event.currentTarget);
    let startDate = await this.getNextWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_drive_test_slot_day(startDate, "calendar_next_week");
  }

  async slotsPreviousWeek(event) {
    event.preventDefault();
    if (event.currentTarget.disabled) {
      return;
    }

    const startingDate = this.getDisplayedWeekStartDate(event.currentTarget);
    let startDate = await this.getPreviousWeekStartDate(startingDate);
    startDate = this.formatDateToString(startDate);
    await this.get_next_drive_test_slot_day(startDate, "calendar_previous_week");
  }

  async fetchAndUpdateCalendar(startingDate, actionWord) {
    const locale = this.getLocale();
    await this.updateNavigationButtons(startingDate);

    const response = await fetch(
      `/us/slots/calendar/${actionWord}/${startingDate}?locale=${locale}&drive_test=true&dashboard=true`,
      {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("slots-day-picker");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async updateNavigationButtons(startingDate) {
    const displayedWeekStartingDate = this.parseDateString(startingDate);
    const previousWeekButton = document.getElementById(
      "slots-picker-previous-week-button"
    );
    const nextWeekButton = document.getElementById("slots-picker-next-week-button");
    const todayButton = document.getElementById("slots-picker-today-button");

    this.element.dataset.driveTestDashboardDisplayedWeekStart =
      this.formatDateToString(displayedWeekStartingDate);

    [previousWeekButton, nextWeekButton, todayButton].forEach((button) => {
      if (!button) return;
      button.dataset.startingDate =
        this.formatDateToString(displayedWeekStartingDate);
    });

    this.updateDashboardNavigationButtons();
  }

  async get_next_drive_test_slot_day(startDate, action) {
    await this.showLoader();

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const schoolToken = document
      .getElementById("us-school-token")
      .innerHTML.trim();
    const locale = this.getLocale();
    const automaticPreferenceInput = document.getElementById(
      "driving_school_booking_gearbox_preference_automatic"
    );
    const isAutomaticSelected = automaticPreferenceInput
      ? automaticPreferenceInput.checked
      : false;
    const eligibilityDate = this.hasEligibilityDateTarget
      ? this.eligibilityDateTarget.value
      : null;
    const selectedOfficeZoneIds = this.hasZoneIdsTarget ? this.zoneIdsTarget.value : "";

    const response = await fetch(
      `/us/select-next-drive-test-slot-day/${schoolToken}?start_time=${startDate}&week_action=${action}&week_change=false&is_automatic=${isAutomaticSelected}&instructor_id=Any&locale=${locale}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          driving_school_token: schoolToken,
          selected_office_zone_ids: selectedOfficeZoneIds,
          is_initial_load: false,
          eligibility_date: eligibilityDate,
          dashboard: true,
        }),
      }
    );

    if (!response.ok) {
      await this.hideLoader();
      return;
    }

    const data = await response.json();
    const startDateIso = data.selected_date;
    const selectedDay = data.selected_day;
    const weekStartDate = data.week_start_date;
    const disabledDays = data.disabled_days;
    const earliestWeekStartDate = data.earliest_week_start_date;
    const latestWeekStartDate = data.latest_week_start_date;
    const timeZone = data.time_zone;

    if (earliestWeekStartDate) {
      this.element.dataset.driveTestDashboardEarliestWeekStart =
        earliestWeekStartDate;
    }

    if (latestWeekStartDate) {
      this.element.dataset.driveTestDashboardLatestWeekStart =
        latestWeekStartDate;
    }

    if (weekStartDate !== null) {
      const actionWord =
        action === "calendar_previous_week" ? "previous_week" : "next_week";
      await this.fetchAndUpdateCalendar(weekStartDate, actionWord);
    }

    await this.disable_week_days(disabledDays);

    let resolvedStartDateIso = startDateIso;
    let resolvedSelectedDay = selectedDay;

    // Returning to the initial week can come back blank from the
    // dashboard week-nav response even though the initial page render had slots.
    // In that one case, reuse the initial dashboard state instead of showing
    // the week as empty.
    if (this.shouldUseInitialWeekFallback(weekStartDate, startDateIso, selectedDay)) {
      resolvedStartDateIso =
        this.element.dataset.driveTestDashboardInitialSelectedDate || null;
      const initialSelectedDay =
        this.element.dataset.driveTestDashboardInitialSelectedDay;
      resolvedSelectedDay =
        initialSelectedDay === undefined || initialSelectedDay === ""
          ? null
          : parseInt(initialSelectedDay, 10);
    }

    const hasSelectedDay =
      resolvedStartDateIso !== null && resolvedSelectedDay !== null;
    if (hasSelectedDay) {
      this.element.dataset.driveTestDashboardCurrentDate = resolvedStartDateIso;
      const dateWithTimeZone = Luxon.DateTime.fromISO(resolvedStartDateIso, {
        zone: timeZone,
      }).startOf("day");
      await this.get_drive_test_slots(null, dateWithTimeZone);
      this.select_slot_day(resolvedSelectedDay);
      this.disableScheduleButton();
    } else {
      this.clearDashboardSlots();
      this.displayDashboardWeekMessage(true);
      this.toggleDashboardNoSlots(false);
      this.disableScheduleButton();
    }

    this.updateDashboardNavigationButtons();
    await this.hideLoader();
  }

  displayDashboardWeekMessage(show) {
    const weekMessage = document.getElementById("no_slots_this_week");
    if (weekMessage) {
      weekMessage.hidden = !show;
    }
  }

  toggleDashboardNoSlots(show) {
    const noSlot = document.getElementById("no_slot_available");
    if (noSlot) {
      noSlot.hidden = !show;
    }
  }

  updateDashboardNavigationButtons() {
    const previousButton = document.getElementById(
      "slots-picker-previous-week-button"
    );
    const nextButton = document.getElementById("slots-picker-next-week-button");
    if (!previousButton || !nextButton) {
      return;
    }

    const earliestWeekStartString =
      this.element.dataset.driveTestDashboardEarliestWeekStart;
    const latestWeekStartString =
      this.element.dataset.driveTestDashboardLatestWeekStart;
    const displayedWeekStartString =
      this.element.dataset.driveTestDashboardDisplayedWeekStart;

    if (!displayedWeekStartString) {
      return;
    }

    const displayedWeekStart = Luxon.DateTime.fromISO(
      displayedWeekStartString
    ).startOf("week");

    let shouldDisablePrevious = false;
    if (earliestWeekStartString) {
      const earliestWeekStart = Luxon.DateTime.fromISO(
        earliestWeekStartString
      ).startOf("week");
      shouldDisablePrevious = displayedWeekStart <= earliestWeekStart;
    }

    let shouldDisableNext = false;
    if (latestWeekStartString) {
      const latestWeekStart = Luxon.DateTime.fromISO(
        latestWeekStartString
      ).startOf("week");
      shouldDisableNext = displayedWeekStart >= latestWeekStart;
    }

    this.setArrowButtonState(previousButton, shouldDisablePrevious);
    this.setArrowButtonState(nextButton, shouldDisableNext);
  }

  getDisplayedWeekStartDate(fallbackButton) {
    return (
      this.element.dataset.driveTestDashboardDisplayedWeekStart ||
      fallbackButton.dataset.startingDate
    );
  }

  clearDashboardSlots() {
    const targetElement = document.getElementById("potential_slots");
    if (targetElement) {
      targetElement.innerHTML = "";
    }
    this.clearSelectedSlot();
  }

  select_slot_day(selectedDay) {
    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    const selectedDayButton = document.getElementById(`day-${selectedDay}`);
    if (!selectedDayButton) {
      return;
    }

    selectedDayButton.classList.add("isActive");
    selectedDayButton.classList.remove("Button--disabled");
    document.getElementById("picked_slot_day").value =
      selectedDayButton.textContent.trim();
    document.getElementById("picked_slot_time").value = "";
  }

  async disable_week_days(disabledDays) {
    this.dayButtonTargets.forEach((button) => {
      button.classList.remove("Button--disabled");
    });

    disabledDays.forEach((day) => {
      const dayButton = document.getElementById(`day-${day}`);
      if (dayButton) {
        if (dayButton.classList.contains("isActive")) {
          return;
        }
        dayButton.classList.add("Button--disabled");
      }
    });
  }

  filter_on_transmission_change() {
    const displayedWeekStart =
      this.element.dataset.driveTestDashboardDisplayedWeekStart ||
      this.element.dataset.driveTestDashboardCurrentDate ||
      this.formatDateToString(new Date());

    this.clearSelectedSlot();
    this.get_next_drive_test_slot_day(displayedWeekStart, "calendar_next_week");
  }

  clearSelectedSlot() {
    this.timeButtonTargets.forEach((button) => {
      button.classList.remove("isActive");
    });

    this.resetPickedSlotFields();
  }

  resetPickedSlotFields() {
    document.getElementById("picked_slot_time").value = "";
    document.getElementById("picked_slot_iso_time").value = "";
    document.getElementById("picked_slot_id").value = "";
    document.getElementById("picked_slot_is_master_lesson").value = "false";
    document.getElementById("picked_slot_master_lesson_id").value = "";
    this.disableScheduleButton();
  }

  disableScheduleButton() {
    if (this.hasNextButtonSlotsTarget) {
      this.nextButtonSlotsTarget.disabled = true;
    }
  }

  enableScheduleButton() {
    if (this.hasNextButtonSlotsTarget) {
      this.nextButtonSlotsTarget.disabled = false;
    }
  }

  getLocale() {
    if (this.hasLocaleTarget) {
      return this.localeTarget.value;
    }

    const localeElement = document.getElementById("us-school-locale");
    if (localeElement && localeElement.value) {
      return localeElement.value;
    }

    return "en";
  }

  setArrowButtonState(button, disabled) {
    if (!button) {
      return;
    }

    button.disabled = disabled;
    if (disabled) {
      button.classList.add("Button--disabled");
    } else {
      button.classList.remove("Button--disabled");
    }
  }

  shouldUseInitialWeekFallback(weekStartDate, startDateIso, selectedDay) {
    if (startDateIso !== null || selectedDay !== null) {
      return false;
    }

    const initialWeekStart =
      this.element.dataset.driveTestDashboardInitialWeekStart;
    const initialSelectedDate =
      this.element.dataset.driveTestDashboardInitialSelectedDate;
    const initialSelectedDay =
      this.element.dataset.driveTestDashboardInitialSelectedDay;

    if (!initialWeekStart || !initialSelectedDate || initialSelectedDay === "") {
      return false;
    }

    return weekStartDate === initialWeekStart;
  }
}
